// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../utils/api';
import { errorMessage } from '../utils/erreurs';
import { filterItems } from '../utils/recherche';
import ChampRecherche from '../components/ChampRecherche';
import DialoguePreparation from '../components/clesDePose/DialoguePreparation';
import KeyWriterPanel from '../components/clesDePose/PanneauGraveur';
import OrdersTable from '../components/clesDePose/TableauOrdres';
import {
  OVERDUE_HOURS, isOverdue, normalizeOrder, effectiveStatus,
} from '../components/clesDePose/commun';

/**
 * Préparation des clés de pose du serveur d'établissement (B2B).
 *
 * L'écran crée le kit, le rattache à l'établissement et télécharge un ordre de
 * fabrication (`order-<serie>.vigeos.json`, schéma
 * `vigeos.ordre-de-fabrication/1`) : série, kit (code et secret), site, API à
 * joindre, nombre de chambres compté par le backend à l'émission. L'atelier ne
 * lit que ce fichier, sans identifiant propre ni accès à l'API.
 *
 * Invariants :
 *  1. Aucune route ne rend le secret d'un kit existant, donc aucun ordre ne se
 *     retélécharge : un compte staff compromis récupérerait sinon l'identité
 *     de tous les serveurs en service. Un ordre perdu s'annule et se refait.
 *  2. Rien ne passe par l'URL (historique, journal nginx, Cloudflare) : POST
 *     sur un chemin fixe, établissement dans le corps, fichier produit en
 *     mémoire depuis la réponse.
 *  3. L'ordre expire en 48 h ; la pose reste acceptée jusqu'à
 *     `pose_possible_jusqu_au` (app/services/pro/ordre_cle.py), puis
 *     l'identité du serveur devient caduque.
 *  4. L'ordre se referme au premier /api/provision/hello du kit, jamais par
 *     un bouton : un état déclaré à la main ment quand la pose échoue.
 *  5. Un établissement reçoit autant de clés qu'il a de serveurs (routage par
 *     assignation des caméras, provision._config_payload_site) ; l'écran
 *     montre l'existant et fait saisir un repère.
 *
 * Le secret n'est jamais affiché, ce qui l'exposerait aux captures, aux
 * partages d'écran et à l'inspecteur du navigateur. Personne ne le tape : il va
 * du fichier à la clé, ce qui autorise ses 192 bits (`token_urlsafe(24)`).
 */

// Routeur `/api/cles-de-pose` (app/main.py) : une route pour lister, une pour
// créer (DialoguePreparation), le site voyageant dans le corps du POST. La
// constante est écrite dans chaque fichier qui appelle :
// `backend/tests/test_console_routes.py` relit ces chemins et ne résout une
// constante que dans le fichier qui la déclare.
const ORDERS_ROUTE = '/cles-de-pose';

const FILTERS = [
  { valeur: '', label: 'Tous' },
  { valeur: 'ouverts', label: 'En cours' },
  { valeur: 'souffrance', label: 'Sans nouvelles' },
  { valeur: 'pose_confirmee', label: 'Posés' },
  { valeur: 'expire', label: 'Expirés' },
];

export default function InstallKeysPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [installOrders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [activeFilter, setFilter] = useState('');

  // Organisations avec leurs sites et leurs chambres, pour choisir
  // l'établissement.
  const [organizations, setOrganizations] = useState([]);
  const [sitesError, setSitesError] = useState('');

  // Annulation : l'ordre visé par la confirmation, et celui dont l'appel est
  // en cours. Deux états, car la fenêtre se ferme avant la fin de l'appel et la
  // ligne doit montrer qu'il se passe quelque chose.
  const [toCancel, setToCancel] = useState(null);
  const [suppression, setSuppression] = useState(null);

  // Dialogue de préparation (voir DialoguePreparation) : null quand il est
  // fermé.
  const [dialogue, setDialogue] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(ORDERS_ROUTE);
      const brut = Array.isArray(res.data) ? res.data : (res.data?.ordres || []);
      setOrders(brut.map(normalizeOrder));
      setError('');
    } catch (e) {
      setOrders([]);
      setError(errorMessage(e, 'Impossible de charger les ordres de fabrication'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrganizations = useCallback(async () => {
    try {
      const res = await api.get('/pro/admin/organisations');
      setOrganizations(Array.isArray(res.data) ? res.data : []);
      setSitesError('');
    } catch (e) {
      // La cause est gardée : un menu vide accuse la base (« aucun
      // établissement en service ») alors qu'un 403 accuse les droits du compte.
      setOrganizations([]);
      setSitesError(errorMessage(e, "Liste des établissements illisible"));
    }
  }, []);

  useEffect(() => {
    loadOrders();
    loadOrganizations();
  }, [loadOrders, loadOrganizations]);

  // Établissements ouverts à la fabrication, à plat : un groupe désactivé ou un
  // site fermé n'a pas de clé à recevoir.
  const sites = useMemo(() => organizations
    .filter((org) => org.is_active !== false)
    .flatMap((org) => (org.sites || [])
      .filter((s) => s.is_active !== false)
      .map((s) => ({
        id: s.id,
        nom: s.name,
        organisation: org.name,
        ville: s.city || '',
        codePostal: s.postal_code || '',
        chambres: s.rooms ?? 0,
      }))), [organizations]);

  // Localisation par établissement. La ligne d'un ordre ne la porte pas (le
  // document ne transporte que le nom du site), mais la barre de recherche
  // promet de chercher dessus : elle est reprise de la liste des
  // établissements. Les ordres d'un site désactivé, absent de cette liste,
  // restent cherchables par nom, série et code, pas par ville.
  const lieuParSite = useMemo(() => {
    const table = {};
    sites.forEach((s) => { table[s.id] = [s.codePostal, s.ville].filter(Boolean).join(' '); });
    return table;
  }, [sites]);

  const openDialog = useCallback((siteId = '') => {
    const site = sites.find((s) => s.id === siteId) || null;
    setDialogue({
      siteId: site ? site.id : '',
      // Repère de pose, libre et facultatif. Pas de champ « nombre de
      // chambres » : le backend les compte sur l'établissement à l'émission et
      // fige ce nombre dans la ligne.
      repere: '',
      enCours: false,
      erreur: '',
      document: null,
      nomFichier: '',
      texteBrut: '',
    });
  }, [sites]);

  // Arrivée depuis la fiche d'un établissement : le site voyage dans l'état du
  // routeur, pas dans l'URL. Une fois consommé, il est retiré pour qu'un
  // rechargement de la page ne rouvre pas le dialogue.
  useEffect(() => {
    const siteId = location.state?.siteId;
    if (!siteId || !sites.length) return;
    openDialog(siteId);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, sites.length, openDialog, navigate]);

  const updateDialog = (patch) => setDialogue((d) => (d ? { ...d, ...patch } : d));

  const filteredRows = useMemo(() => {
    const byState = installOrders.filter((o) => {
      const statusValue = effectiveStatus(o);
      if (activeFilter === 'ouverts' && (statusValue === 'expire' || statusValue === 'pose_confirmee')) return false;
      if (activeFilter === 'souffrance' && !isOverdue(o)) return false;
      if (activeFilter === 'pose_confirmee' && statusValue !== 'pose_confirmee') return false;
      if (activeFilter === 'expire' && statusValue !== 'expire') return false;
      return true;
    });
    return filterItems(byState, recherche, (o) => [
      o.serie, o.siteNom, o.organisation, o.kitCode, o.emisPar, o.repere,
      lieuParSite[o.siteId],
    ]);
  }, [installOrders, recherche, activeFilter, lieuParSite]);

  const overdueCount = useMemo(() => installOrders.filter(isOverdue).length, [installOrders]);

  /**
   * Annule une clé préparée : le backend retire le kit dans le même geste. Le
   * secret a quitté le serveur à la création (disque, corbeille, clé gravée) ;
   * supprimer la ligne seule le laisserait valable. Une pose confirmée est
   * refusée par le backend (409) en plus du bouton grisé, qui seul laisserait
   * passer tout appel venu d'ailleurs.
   */
  const cancel = async (order) => {
    setToCancel(null);
    setSuppression(order.id);
    try {
      const r = await api.delete(`${ORDERS_ROUTE}/${order.id}`);
      const kit = r.data?.kit?.code || order.kitCode;
      setToast({
        texte: `Clé ${order.serie} annulée : le kit ${kit} est retiré, son secret n'ouvre plus rien.`,
        gravite: 'success',
      });
      loadOrders();
    } catch (e) {
      setToast({
        texte: errorMessage(e, `Annulation de ${order.serie} refusée`),
        gravite: 'error',
      });
    } finally {
      setSuppression(null);
    }
  };

  const closeDialog = () => {
    if (dialogue?.enCours) return;
    // Le document et son texte meurent ici : c'est la seule copie que l'onglet
    // détenait.
    setDialogue(null);
  };

  /**
   * Ferme la fenêtre une fois le fichier remis, sans accusé de remise : la
   * remise est la création, datée par le serveur (`telecharge_le`), et le
   * navigateur ne sait pas si le fichier a fini sur un disque. L'ordre reste
   * « à poser » jusqu'au premier /hello du serveur, et passe « sans
   * nouvelles » au bout de 24 h.
   */
  const confirmerRemise = () => {
    setDialogue(null);
    loadOrders();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Clés de pose</Typography>
          <Typography variant="body2" color="text.secondary">
            Préparer une clé crée le serveur de l'établissement et télécharge son
            ordre de fabrication. L'atelier grave la clé depuis ce fichier : il ne
            parle jamais à l'API.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Recharger">
            <span>
              <IconButton onClick={loadOrders} disabled={loading}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog('')}>
            Préparer une clé
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {overdueCount > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <AlertTitle>
            {overdueCount} clé{overdueCount > 1 ? 's' : ''} préparée{overdueCount > 1 ? 's' : ''} depuis plus de {OVERDUE_HOURS} h dont aucun serveur n'a dit bonjour
          </AlertTitle>
          Clé jamais gravée, jamais partie, ou serveur démarré sans joindre l'API :
          dans tous les cas la pose n'a pas eu lieu. Passé 48 h l'ordre expire et le
          secret n'est plus lisible nulle part ; il faut alors en préparer un autre.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Rechercher par établissement, localisation, série ou code du serveur"
          compte={filteredRows.length}
          total={installOrders.length}
          nomObjet="ordre"
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>État</InputLabel>
          <Select value={activeFilter} label="État" onChange={(e) => setFilter(e.target.value)}>
            {FILTERS.map((f) => <MenuItem key={f.valeur} value={f.valeur}>{f.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <OrdersTable
        ordres={installOrders}
        filtrees={filteredRows}
        chargement={loading}
        suppression={suppression}
        onPreparer={() => openDialog('')}
        onAnnuler={setToCancel}
      />

      <KeyWriterPanel onToast={setToast} />

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
        Un ordre ne se retélécharge pas : le secret du kit n'existe que dans le
        fichier remis à la création, et aucune route ne le rend une seconde fois.
        Un ordre perdu s'annule, et on en prépare un autre.
      </Typography>

      <DialoguePreparation
        dialogue={dialogue}
        sites={sites}
        erreurSites={sitesError}
        ordres={installOrders}
        onMaj={updateDialog}
        onFermer={closeDialog}
        onCree={loadOrders}
        onRemis={confirmerRemise}
      />

      {/* Confirmation d'annulation : elle nomme ce qui disparaît et ce qui
          reste. Le kit reste en base, retiré et daté, pour garder la trace de
          ce qui a été fabriqué. */}
      <Dialog open={!!toCancel} onClose={() => setToCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Annuler la clé {toCancel?.serie} ?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {toCancel?.siteNom}{toCancel?.repere ? ` · ${toCancel.repere}` : ''}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Le kit {toCancel?.kitCode} sera retiré</AlertTitle>
            C'est ce qui annule vraiment la clé : son secret cesse d'ouvrir le
            provisioning, et une clé déjà gravée avec cet ordre devient inerte. Un
            serveur posé avec elle ne pourrait plus se configurer.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            La trace n'est pas perdue : le kit reste dans le parc, retiré et daté.
            Ce qui disparaît, c'est la ligne de suivi d'un ordre qui n'a rien
            produit. Il n'y a pas de retour en arrière : pour ce site, il faudra
            préparer une nouvelle clé.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToCancel(null)}>Garder la clé</Button>
          <Button variant="contained" color="error" onClick={() => cancel(toCancel)}>
            Annuler la clé et retirer le kit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.gravite || 'success'} onClose={() => setToast(null)}>
          {toast?.texte}
        </Alert>
      </Snackbar>
    </Box>
  );
}
